import { MatrixEvent } from 'matrix-js-sdk';
import { ChatRoom } from './chat-room';

/**
 * Globaler Chat-Zustand.
 * Begründung: RxJS-Architektur (keine unmittelbare AFO-Anforderung)
 */
export class ChatState {
  private constructor(
    private readonly _rooms: Map<string, ChatRoom>,
    private readonly _currentRoomId: string | undefined
  ) {}

  static new(): ChatState {
    return new ChatState(new Map(), undefined);
  }

  get rooms(): Map<string, ChatRoom> {
    return this._rooms;
  }

  get currentRoomId(): string | undefined {
    return this._currentRoomId;
  }

  getCurrentRoom(): ChatRoom | undefined {
    return this._rooms.get(this._currentRoomId as string);
  }

  getRoomById(roomId?: string): ChatRoom | undefined {
    return roomId ? this._rooms.get(roomId) : undefined;
  }

  getChatRooms(): ChatRoom[] {
    return Array.from(this._rooms.values());
  }

  /****************************************************************************
   * Reducers
   ****************************************************************************/

  reduceByNewCurrentRoomId(newCurrentRoomId: string): ChatState {
    if (newCurrentRoomId === this._currentRoomId) {
      return this;
    }

    return new ChatState(this._rooms, newCurrentRoomId);
  }

  reduceByCreateRoom(roomId: string): ChatState {
    const room = this._rooms.get(roomId);

    if (room) {
      console.warn(`reduceByCreateRoom: Matrix-Raum zu roomId=${roomId} bereits vorhanden`, room);
      return this;
    }

    const newRooms = new Map([...this._rooms, [roomId, ChatRoom.new()]]);

    return new ChatState(newRooms, this._currentRoomId);
  }

  reduceByDeleteRoom(deleteRoomId: string): ChatState {
    if (!this._rooms.has(deleteRoomId)) {
      return this;
    }

    const newRooms = this.deleteFromMap(this._rooms, deleteRoomId);

    const newCurrentRoomId = deleteRoomId === this._currentRoomId ? undefined : this._currentRoomId;

    return new ChatState(newRooms, newCurrentRoomId);
  }

  reduceByExchangeRoom(roomId: string, newRoom: ChatRoom): ChatState {
    const newRooms = new Map([...this._rooms, [roomId, newRoom]]);

    return new ChatState(newRooms, this._currentRoomId);
  }

  /**
   * 1. Füge neue ChatMessage hinzu, die als Info-Nachricht angezeigt wird
   *    (invite/join/leave)
   * 2. Aktualisiere ChatRoom mit neu berechnetem ModelRoom und ApiRoom
   */
  reduceByRoomMemberEvent(event: MatrixEvent): ChatState {
    return this.reduceByNewMessageEvent(event);
  }

  /**
   * Füge neue ChatMessage hinzu. Ggf. füge vorher neuen ChatRoom hinzu.
   */
  reduceByNewMessageEvent(event: MatrixEvent): ChatState {
    const roomId = event.getRoomId()!;
    const room = this._rooms.get(roomId) ?? ChatRoom.new();

    const newRoom = room.reduceByNewMessageEvent(event);

    if (newRoom === room) {
      // Event ist bereits im Store.
      return this;
    }

    const newRooms = new Map([...this._rooms, [roomId, newRoom]]);

    return new ChatState(newRooms, this._currentRoomId);
  }

  /**
   * Erzwingt State-Update für ein in-place entschlüsseltes Event (MatrixEventEvent.Decrypted).
   * Das Event-Objekt wurde vom SDK mutiert und hat jetzt getClearContent().
   */
  reduceByDecryptedEvent(event: MatrixEvent): ChatState {
    const roomId = event.getRoomId();
    if (!roomId) {
      return this;
    }
    const room = this._rooms.get(roomId);
    if (!room) {
      return this;
    }

    const newRooms = new Map([...this._rooms, [roomId, room.reduceByDecryptedEvent(event)]]);
    return new ChatState(newRooms, this._currentRoomId);
  }

  /**
   * Erweitere bestehende ChatMessage um editMessageEvent.
   */
  reduceByEditMessageEvent(event: MatrixEvent): ChatState {
    const roomId = event.getRoomId();
    if (!roomId) {
      console.warn('roomId fehlt');
      return this;
    }

    // Wenn kein Raum gefunden wird, bedeutet das, dass die Original-Nachricht noch nicht eingespeist worden ist.
    // Es wird dennoch eine ChatMessage erzeugt, um das Edit-Event aufzunehmen, und dazu ggf. auch ein neuer ChatRoom.
    const room = this._rooms.get(roomId) ?? ChatRoom.new();

    const newRoom = room.reduceByEditMessageEvent(event);
    if (newRoom === room) {
      return this;
    }

    const newRooms = new Map([...this._rooms, [roomId, newRoom]]);

    return new ChatState(newRooms, this._currentRoomId);
  }

  /**
   * Erweitere bestehenden Raum um Reaction.
   */
  reduceByReactionEvent(event: MatrixEvent): ChatState {
    // Beim Laden der LiveTimeline werden alle Events eingespeist einschließlich
    // derer, die bereits redacted worden sind. Diese können ignoriert werden.
    // Auch deren nachfolgendes Redaction-Event kann ignoriert werden.
    // Ignorieren wird dadurch erreicht, dass der State (this) _unverändert_
    // zurückgegeben wird. Bei distinctUntilChanged() ist dann Schluss.
    if (event.isRedacted()) {
      return this;
    }

    const roomId = event.getRoomId()!;
    const room = this._rooms.get(roomId);
    if (!room) {
      console.warn('reduceByReactionEvent: Raum nicht gefunden.', event);
      return this;
    }

    const newRoom = room.reduceByReactionEvent(event);
    if (newRoom === room) {
      return this;
    }

    const newRooms = new Map([...this._rooms, [roomId, newRoom]]);

    return new ChatState(newRooms, this._currentRoomId);
  }

  /**
   * Eine Redaction löscht entweder eine Message oder cancelt eine Reaction.
   * Bezieht sich die Redaction auf eine (Original-) Message (erster Eintrag
   * in ChatMessage.messages), wird die ChatMessage als gelöscht markiert.
   * Außerdem enthält die Redaction in diesem Fall einen "Reason"-String,
   * der anstelle der gelöschten Nachricht angezeigt wird.
   * Bezieht sich die Redaction auf eine Reaction, wird die Reaction entfernt.
   */
  reduceByRedactionEvent(event: MatrixEvent): ChatState {
    const roomId = event.getRoomId()!;
    const room = this._rooms.get(roomId);
    if (!room) {
      console.warn('reduceByRedactionEvent: Raum nicht gefunden.', event);
      return this;
    }

    const newRoom = room.reduceByRedactionEvent(event);
    if (newRoom === room) {
      return this;
    }

    const newRooms = new Map([...this._rooms, [roomId, newRoom]]);

    return new ChatState(newRooms, this._currentRoomId);
  }

  /****************************************************************************
   * Hilfsroutinen
   ****************************************************************************/

  /**
   * Immutable delete
   */
  private deleteFromMap<K, V>(original: Map<K, V>, key: K): Map<K, V> {
    const copy = new Map(original); // flache Kopie
    copy.delete(key); // mutiert Kopie
    return copy;
  }
}
